import { useEffect, useState } from 'react';
import { api, members as membersApi, type AccessModule, type AccessRow, type Member, type MemberDevice,
  type MemberInput, type MemberStatus, type MetaResponse } from '@tvx/api-client';
import { Modal } from '../components/Modal';
import { stampDay, parseStamp } from '../lib/time';

const BLANK: MemberInput = {
  name: '', email: '', mobile: '', status: 'active', note: '',
};

const MODULE_LABEL: Record<string, string> = {
  calls: 'Trade Calls', news: 'News & Analysis', learning: 'Learning',
};

const GEN_ALPHABET = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function MemberForm({ member, defaultLimit = null, onCancel, onSave, onDone, onChanged }: {
  member: Member | null;
  /** The device limit in Settings, when the list could tell; null when not. */
  defaultLimit?: number | null;
  onCancel: () => void;
  onSave: (f: MemberInput) => Promise<void>;
  onDone: () => void;
  /** Something saved on its own (seats, devices), so the list can catch up. */
  onChanged?: () => void;
}) {
  const [f, setF] = useState<MemberInput>(() => member
    ? {
      name: member.name, email: member.email, mobile: member.mobile,
      status: member.status, note: member.note,
    }
    : BLANK);
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [meta, setMeta] = useState<MetaResponse | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  /* The password that was just set, kept on screen until it is dismissed.
     Saving hashes it; from that point nobody can read it back, so closing
     straight away throws away the only moment it can be passed on. */
  const [handover, setHandover] = useState('');
  /* What the reveal came back with: either the password or the reason there
     is nothing to show. Never fetched until the button is pressed, because
     the fetch is what gets written to the audit trail. */
  const [revealed, setRevealed] = useState<{ password: string; reason: string } | null>(null);
  const [revealing, setRevealing] = useState(false);

  useEffect(() => { void api.get<MetaResponse>('/meta').then(setMeta).catch(() => {}); }, []);

  const set = <K extends keyof MemberInput>(k: K, v: MemberInput[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  async function submit() {
    if (password && password.length < 8) {
      setError('A password must be at least 8 characters.');
      return;
    }
    setError('');
    setStatus('Saving…');
    try {
      // Sent only when it was typed, so a blank box can never blank a password.
      await onSave(password ? { ...f, password } : f);
      if (password) { setStatus(''); setHandover(password); } else onDone();
    } catch (e) {
      setStatus('');
      setError((e as Error).message);
    }
  }

  const title = member ? `Edit member · ${member.name || member.email}` : 'New member';

  /* Saved, and a password was set. The member row is already updated; what is
     left is handing the value over before it stops existing in readable form. */
  if (handover) {
    return (
      <Modal title={title} onClose={onDone} footer={
        <>
          <span className="mdl-spacer" />
          <button className="b bg" type="button" onClick={onDone}>Done</button>
        </>
      }>
        <p style={{ fontSize: 14, marginTop: 0 }}>
          Saved. This is the only time this password can be read — it is hashed from here on,
          so copy it now if {f.name || 'this member'} needs to be told.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
          <input className="inp" readOnly value={handover} style={{ flex: 1, minWidth: 0,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 14 }} />
          <button
            className="b bg"
            type="button"
            style={{ height: 34, padding: '0 13px', fontSize: 12, borderRadius: 999 }}
            onClick={() => {
              void navigator.clipboard.writeText(handover);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="hint" style={{ marginTop: 10 }}>
          Every other device this member was signed in on has been signed out.
        </p>
      </Modal>
    );
  }

  return (
    <Modal title={title} onClose={onCancel} footer={
      <>
        <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
        <span className="mdl-spacer" />
        <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
        <button className="b bg" type="button" onClick={() => void submit()}>Save member</button>
      </>
    }>
      {error ? <div className="err">{error}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <Field label="Name"><input className="inp" value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus /></Field>
        <Field label="Email"><input className="inp" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field label="Mobile"><input className="inp" value={f.mobile} onChange={(e) => set('mobile', e.target.value)} /></Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <Field label="Status">
          <select className="inp" value={f.status} onChange={(e) => set('status', e.target.value as MemberStatus)}>
            {(meta?.memberStatus ?? [f.status]).map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      {member ? <VerifyPanel member={member} email={f.email} mobile={f.mobile} onChanged={onChanged} /> : null}

      {member ? <AccessPanel member={member} modules={meta?.accessModules ?? []} /> : null}

      {member ? <SecurityPanel member={member} defaultLimit={defaultLimit} onChanged={onChanged} /> : null}



      <div className="fld">
        <label className="lbl">{member ? 'New password' : 'Password'}</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="inp"
            type={shown ? 'text' : 'password'}
            autoComplete="new-password"
            style={{ flex: 1, minWidth: 0 }}
            placeholder={member
              ? (member.has_password ? 'Leave blank to keep unchanged' : 'Set a password so member can sign in')
              : 'At least 8 characters'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            className="b bo"
            type="button"
            style={{ height: 34, padding: '0 12px', fontSize: 12, borderRadius: 999 }}
            onClick={() => setShown((v) => !v)}
          >
            {shown ? 'Hide' : 'Show'}
          </button>
          <button
            className="b bo"
            type="button"
            style={{ height: 34, padding: '0 12px', fontSize: 12, borderRadius: 999 }}
            onClick={() => {
              const n = new Uint32Array(12);
              crypto.getRandomValues(n);
              setPassword([...n].map((v) => GEN_ALPHABET[v % GEN_ALPHABET.length]).join(''));
              setShown(true);
            }}
          >
            Generate
          </button>
          {member && member.has_password && !password ? (
            <button
              className="b bo"
              type="button"
              style={{ height: 34, padding: '0 12px', fontSize: 12, borderRadius: 999 }}
              disabled={revealing}
              title="Show the password this member currently uses"
              onClick={() => {
                setRevealing(true);
                void membersApi.password(member.id)
                  .then(setRevealed)
                  .catch((e: Error) => setRevealed({ password: '', reason: e.message }))
                  .finally(() => setRevealing(false));
              }}
            >
              {revealing ? 'Reading…' : 'Reveal current'}
            </button>
          ) : null}
          {password ? (
            <button
              className="b bg"
              type="button"
              style={{ height: 34, padding: '0 13px', fontSize: 12, borderRadius: 999 }}
              onClick={() => {
                void navigator.clipboard.writeText(password);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          ) : null}
        </div>
        {revealed ? (
          revealed.password ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
              <input className="inp" readOnly value={revealed.password} style={{ flex: 1, minWidth: 0,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 14 }} />
              <button
                className="b bg"
                type="button"
                style={{ height: 34, padding: '0 13px', fontSize: 12, borderRadius: 999 }}
                onClick={() => {
                  void navigator.clipboard.writeText(revealed.password);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          ) : <p className="hint" style={{ marginTop: 8 }}>{revealed.reason}</p>
        ) : null}
        <p className="hint" style={{ marginTop: 6, marginBottom: 16 }}>
          {member
            ? 'Reveal current shows the password this member signs in with, and is written to the audit '
              + 'trail. Leave the box blank to keep that password; setting a new one signs this member out '
              + 'of other devices.'
            : 'Leave blank to create account without a password.'}
        </p>
      </div>

      <Field label="Internal note">
        <textarea className="inp ta" rows={2} value={f.note} onChange={(e) => set('note', e.target.value)} />
      </Field>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="fld"><label className="lbl">{label}</label>{children}</div>;
}

/**
 * Whether their email and mobile are confirmed, and the way to confirm either
 * by hand — a code that never arrived, or a number checked over the phone.
 * Each press saves on its own and is written to the audit trail. Editing an
 * address above and saving takes its confirmation back.
 */
function VerifyPanel({ member, email, mobile, onChanged }: {
  member: Member; email: string; mobile: string; onChanged?: () => void;
}) {
  const [on, setOn] = useState({ email: member.email_verified, mobile: member.mobile_verified });
  const [busy, setBusy] = useState<'email' | 'mobile' | null>(null);
  const [error, setError] = useState('');
  const changed = email !== member.email || mobile !== member.mobile;

  async function flip(k: 'email' | 'mobile') {
    setBusy(k); setError('');
    try {
      const r = await membersApi.verify(member.id, { [k]: !on[k] });
      setOn({ email: r.member.email_verified, mobile: r.member.mobile_verified });
      onChanged?.();
    } catch (e) { setError((e as Error).message); }
    setBusy(null);
  }

  const row = (k: 'email' | 'mobile', label: string, value: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 13 }}>
      <span>
        {label}
        <span className="muted" style={{ marginLeft: 8, fontSize: 11.5 }}>{value || 'none'}</span>
        <span className="tg" style={{ marginLeft: 10, background: on[k] ? 'rgba(62,207,142,.14)' : undefined }}>
          {on[k] ? 'confirmed' : 'not confirmed'}
        </span>
      </span>
      <button className={`b ${on[k] ? 'bo' : 'bg'} bs`} type="button" style={{ fontSize: 11, padding: '3px 11px' }}
        disabled={busy !== null || changed || (!on[k] && !value)} onClick={() => void flip(k)}>
        {busy === k ? 'Saving…' : on[k] ? 'Remove confirmation' : 'Confirm'}
      </button>
    </div>
  );

  return (
    <div className="crd" style={{ padding: '16px 18px', marginTop: 4 }}>
      <div className="kh">Confirmed contact</div>
      {error ? <div className="err" style={{ marginTop: 10 }}>{error}</div> : null}
      <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
        {row('email', 'Email', member.email)}
        {row('mobile', 'Mobile', member.mobile)}
      </div>
      {changed ? <p className="hint" style={{ marginTop: 8 }}>Save the new address first, then confirm it.</p> : null}
    </div>
  );
}

/**
 * What this member holds, and the way to put it right by hand.
 *
 * A payment is the usual way access opens. This is for the rest: a bank
 * transfer that never became a payment row, a goodwill month, a mistake to
 * undo. Every grant and every revoke is written to the audit trail.
 */
function AccessPanel({ member, modules }: { member: Member; modules: AccessModule[] }) {
  const [rows, setRows] = useState<AccessRow[] | null>(null);
  const [kind, setKind] = useState<'module' | 'course' | 'batch'>('module');
  const [ref, setRef] = useState<string>(modules[0] ?? 'calls');
  const [days, setDays] = useState(30);
  const [lists, setLists] = useState<{ courses: { id: number; title: string }[]; batches: { id: number; name: string }[] }>(
    { courses: [], batches: [] },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const r = await api.get<{ access: AccessRow[]; courses: typeof lists.courses; batches: typeof lists.batches }>(
        `/members/${member.id}/access`,
      );
      setRows(r.access);
      setLists({ courses: r.courses, batches: r.batches });
    } catch (e) { setError((e as Error).message); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [member.id]);

  const options = kind === 'module'
    ? modules.map((m) => ({ value: m, label: MODULE_LABEL[m] ?? m }))
    : kind === 'course'
      ? lists.courses.map((c) => ({ value: String(c.id), label: c.title }))
      : lists.batches.map((b) => ({ value: String(b.id), label: b.name }));

  async function grant() {
    setBusy(true);
    setError('');
    try {
      const r = await api.post<{ access: AccessRow[] }>(`/members/${member.id}/access`, {
        kind, ref: ref || options[0]?.value, days: kind === 'module' ? days : 0,
      });
      setRows(r.access);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  async function revoke(row: AccessRow) {
    setBusy(true);
    setError('');
    try {
      const r = await api.del<{ access: AccessRow[] }>(
        `/members/${member.id}/access?kind=${encodeURIComponent(row.kind)}&ref=${encodeURIComponent(row.ref)}`,
      );
      setRows(r.access);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="crd" style={{ padding: '16px 18px', marginTop: 4 }}>
      <div className="kh">Access</div>
      {error ? <div className="err" style={{ marginTop: 10 }}>{error}</div> : null}

      {rows === null ? <p className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>…</p>
        : rows.length === 0 ? (
          <p className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
            Nothing open. Everything is bought on its own — a pass, a course, a seat in a batch.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {rows.map((r) => (
              <div key={`${r.kind}:${r.ref}`} style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', gap: 10, fontSize: 13 }}>
                <span>
                  {r.label}
                  <span className="muted" style={{ marginLeft: 8, fontSize: 11.5 }}>
                    {r.expires_at ? `until ${r.expires_at}` : 'kept'}{r.live ? '' : ' · ended'}
                  </span>
                </span>
                <button className="b bo bs" type="button" disabled={busy} onClick={() => void revoke(r)}
                  style={{ fontSize: 11, padding: '3px 9px' }}>Remove</button>
              </div>
            ))}
          </div>
        )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="inp" style={{ width: 'auto' }} value={kind}
          onChange={(e) => { const k = e.target.value as typeof kind; setKind(k); setRef(''); }}>
          <option value="module">Pass</option>
          <option value="course">Course</option>
          <option value="batch">Batch</option>
        </select>
        <select className="inp" style={{ width: 'auto', flex: 1, minWidth: 140 }}
          value={ref || options[0]?.value || ''} onChange={(e) => setRef(e.target.value)}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {kind === 'module' ? (
          <input className="inp" style={{ width: 90 }} type="number" min={0} value={days}
            title="Days — 0 never runs out" onChange={(e) => setDays(Number(e.target.value))} />
        ) : null}
        <button className="b bg bs" type="button" disabled={busy || !options.length} onClick={() => void grant()}>
          Open it
        </button>
      </div>
    </div>
  );
}

/** "3 hours ago", or the day — when a device was last used. */
function lastUsed(v: string) {
  const d = parseStamp(v);
  if (!d) return '—';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 24 ? `${hrs} h ago` : stampDay(v);
}

/**
 * A member's sign-in security, by hand: how many devices they may be signed
 * in on, two-step sign-in switched off for somebody who lost their phone, and
 * the devices themselves. Each change saves on its own — none of it waits for
 * "Save member" — and each is written to the audit trail.
 */
function SecurityPanel({ member, defaultLimit, onChanged }: {
  member: Member; defaultLimit: number | null; onChanged?: () => void;
}) {
  const [limit, setLimit] = useState(member.device_limit == null ? '' : String(member.device_limit));
  const [saved, setSaved] = useState(limit);
  const [twoFactor, setTwoFactor] = useState(member.two_factor);
  const [devices, setDevices] = useState<MemberDevice[] | null>(null);
  const [effective, setEffective] = useState(member.device_limit_effective);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void membersApi.sessions(member.id)
      .then((r) => { setDevices(r.devices); setEffective(r.limit); })
      .catch((e: Error) => setError(e.message));
  }, [member.id]);

  async function run(what: string, fn: () => Promise<void>) {
    setBusy(what);
    setError('');
    try { await fn(); onChanged?.(); } catch (e) { setError((e as Error).message); }
    setBusy(null);
  }

  const saveLimit = () => run('limit', async () => {
    const r = await membersApi.security(member.id, { device_limit: limit.trim() === '' ? '' : Number(limit) });
    const v = r.member.device_limit == null ? '' : String(r.member.device_limit);
    setLimit(v); setSaved(v); setEffective(r.member.device_limit_effective);
  });
  const twoFactorOff = () => run('2fa', async () => {
    const r = await membersApi.security(member.id, { two_factor: false });
    setTwoFactor(r.member.two_factor);
  });
  const signOut = (id: string | 'all') => run(id, async () => {
    const r = await membersApi.signOut(member.id, id);
    setDevices(r.devices);
  });

  const inUse = devices?.length ?? member.devices_in_use;
  const def = defaultLimit == null ? 'Default' : `Default (${defaultLimit || 'no limit'})`;

  return (
    <div style={{ margin: '18px 0', padding: '16px 0', borderTop: '1px solid var(--line)',
      borderBottom: '1px solid var(--line)' }}>
      <div className="kh">Sign-in &amp; security</div>
      {error ? <div className="err" style={{ marginTop: 10 }}>{error}</div> : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 12 }}>
        <div style={{ flex: '0 1 200px' }}>
          <label className="lbl" htmlFor={`devices-${member.id}`}>Devices</label>
          <input id={`devices-${member.id}`} className="inp" type="number" min={0} max={50} step={1}
            inputMode="numeric" placeholder={def} value={limit} onChange={(e) => setLimit(e.target.value)} />
        </div>
        <button className="b bo bs" type="button" disabled={busy !== null || limit === saved}
          onClick={() => void saveLimit()}>{busy === 'limit' ? 'Saving…' : 'Set'}</button>
      </div>
      <p className="hint" style={{ marginTop: 6 }}>
        How many devices they can be signed in on at once. Blank follows Settings; 0 is no limit.
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
        marginTop: 14, fontSize: 13 }}>
        <span>
          Two-step sign-in
          <span className="muted" style={{ marginLeft: 8, fontSize: 11.5 }}>
            {twoFactor ? 'on — a code after the password' : 'off — the member turns it on in their profile'}
          </span>
        </span>
        {twoFactor ? (
          <button className="b bo bs" type="button" disabled={busy !== null} onClick={() => void twoFactorOff()}
            style={{ fontSize: 11, padding: '3px 9px' }}>{busy === '2fa' ? 'Turning off…' : 'Turn off'}</button>
        ) : null}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 16 }}>
        <span className="lbl" style={{ margin: 0 }}>
          Devices · {effective ? `${inUse} of ${effective} in use` : `${inUse} in use · no limit`}
        </span>
        {devices?.length ? (
          <button className="b bo bs" type="button" disabled={busy !== null} onClick={() => void signOut('all')}
            style={{ fontSize: 11, padding: '3px 9px' }}>{busy === 'all' ? 'Signing out…' : 'Sign out everywhere'}</button>
        ) : null}
      </div>
      {devices === null ? <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>…</p>
        : devices.length === 0 ? (
          <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>Not signed in anywhere.</p>
        ) : (
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {devices.map((d) => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 10, fontSize: 13 }}>
                <span>
                  {d.label}
                  <span className="muted" style={{ marginLeft: 8, fontSize: 11.5 }}>
                    last used {lastUsed(d.last_seen_at)} · since {stampDay(d.created_at)}
                  </span>
                </span>
                <button className="b bo bs" type="button" disabled={busy !== null} onClick={() => void signOut(d.id)}
                  style={{ fontSize: 11, padding: '3px 9px' }}>{busy === d.id ? 'Signing out…' : 'Sign out'}</button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
